import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../contexts/TourContext';
import { useTranslatedSteps } from './stepsTour';
import {
  Box,
  Typography,
  Paper,
  Container,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  Fab,
  Alert,
  Divider,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Checkbox,
  FormGroup,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import CheckIcon from '@mui/icons-material/Check';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from '../contexts/TranslationContext';
import api, { behavior, AgentBehavior, BehaviorFilterItem } from '../services/api';
import { enqueueSnackbar } from 'notistack';
import { toPersianDigits } from '../utils/validation';

export const BehaviorContent: React.FC = () => {
  const { t, currentLanguage } = useTranslation();

  const displayTime = (time: string) =>
    currentLanguage === 'fa' ? toPersianDigits(time) : time;
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams] = useSearchParams();
  const [behaviorSettings, setBehaviorSettings] = useState<AgentBehavior>({
    conversion_style: null,
    sales_role: null,
    sales_methodology: null,
    answer_length: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [availableFilters, setAvailableFilters] = useState<BehaviorFilterItem[]>([]);
  const [rejexCheckboxes, setRejexCheckboxes] = useState<{ [key: string]: boolean }>({});
  const [timeRanges, setTimeRanges] = useState<Array<{ start: string; end: string }>>([
    { start: '', end: '' },
  ]);
  const [timeErrors, setTimeErrors] = useState<Array<{ start: string; end: string }>>([
    { start: '', end: '' },
  ]);
  const [selectedRestDay, setSelectedRestDay] = useState<string>('SATURDAY');
  const [showTimeConfigurator, setShowTimeConfigurator] = useState<boolean>(false);
  const [submittingSchedule, setSubmittingSchedule] = useState<boolean>(false);
  // Rest times list (UI-only for now)
  interface RestTimeEntry {
    id: number;
    rest_day: string; // localized label
    weekday_key: string; // original key from API (e.g., 'saturday')
    start_time: string;
    end_time: string;
  }
  const [restTimes, setRestTimes] = useState<RestTimeEntry[]>([]);
  const [currentTab, setCurrentTab] = useState<number>(0);

  const mapWeekdayToFa = (weekday: string) => {
    const key = weekday.toLowerCase();
    const map: { [k: string]: string } = {
      saturday: t('behavior.advanced_setting.days.saturday'),
      sunday: t('behavior.advanced_setting.days.sunday'),
      monday: t('behavior.advanced_setting.days.monday'),
      tuesday: t('behavior.advanced_setting.days.tuesday'),
      wednesday: t('behavior.advanced_setting.days.wednesday'),
      thursday: t('behavior.advanced_setting.days.thursday'),
      friday: t('behavior.advanced_setting.days.friday'),
    };
    return map[key] || weekday;
  };

  const fetchRestTimes = async () => {
    try {
      const res = await api.get('/auto-response/internal/schedule-rules');
      const data = Array.isArray(res.data) ? res.data : (res.data?.items || []);
      const flattened: RestTimeEntry[] = [];
      let idCounter = 1;
      data.forEach((item: any) => {
        const weekday = mapWeekdayToFa(item.weekday || '');
        (item.time_ranges || []).forEach((tr: any) => {
          flattened.push({
            id: idCounter++,
            rest_day: weekday,
            weekday_key: String(item.weekday || ''),
            start_time: tr.start,
            end_time: tr.end,
          });
        });
      });
      setRestTimes(flattened);
    } catch (e) {
      // silent fail; table will stay empty
    }
  };

  let flag_aida_notFound = false;
  let flag_sessionExpired = false;
  useEffect(() => {
    fetchBehaviorSettings();
  }, []);

  useEffect(() => {
    fetchRestTimes();
  }, []);

  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  // Handle tab query parameter
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam !== null) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 1) {
        setCurrentTab(tabIndex);
      }
    }
  }, [searchParams]);

  const fetchBehaviorSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [behaviorResponse, availableFiltersResponse, activeFiltersResponse] = await Promise.all([
        behavior.getBehavior(),
        behavior.getAvailableFilters(),
        behavior.getActiveFilters().catch(() => ({ data: {} })), // Handle if no active filters exist
      ]);
      
      
      // Set behavior settings
      setBehaviorSettings({
        conversion_style: behaviorResponse.data.conversion_style || null,
        sales_role: behaviorResponse.data.sales_role || null,
        sales_methodology: behaviorResponse.data.sales_methodology || null,
        answer_length: behaviorResponse.data.answer_length || null,
      });
      
      // Set available filters
      
      setAvailableFilters(availableFiltersResponse.data);
      
      // Initialize checkboxes with active filters
      const initialCheckboxes: { [key: string]: boolean } = {};
      const activeData = activeFiltersResponse.data || {};
      
      availableFiltersResponse.data.forEach((filter) => {
        initialCheckboxes[filter.name] = (activeData as { [key: string]: boolean })[filter.name] || false;
      });
      
      setRejexCheckboxes(initialCheckboxes);
      
    } catch (error: any) {
      console.error('Error:', error);
      console.error('Error message:', error.message);
      console.error('Error response:', error.response);
      console.error('Error stack:', error.stack);
      
      if (error.response?.status === 500) {
        if (!flag_aida_notFound) {
          enqueueSnackbar(t('behavior.error.Aida_notFound'), { 
            variant: 'error',
            autoHideDuration: 4000,
            style: {
              padding: '10px',
              marginTop: '20px', // Add margin top
            },
          });
          flag_aida_notFound = true;
        }
      } else {
        if (!flag_sessionExpired) {
          enqueueSnackbar(t('behavior.error.sessionExpired'), { 
            variant: 'error',
            autoHideDuration: 4000,
            style: {
              padding: '10px',
              marginTop: '20px', // Add margin top
            },
          });
          flag_sessionExpired = true;
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (value: string): string => {
    // Remove all non-numeric characters except colon
    let cleaned = value.replace(/[^\d:]/g, '');
    
    // Handle empty string
    if (!cleaned) return '';
    
    // If colon exists, handle it
    if (cleaned.includes(':')) {
      const parts = cleaned.split(':');
      const hours = parts[0] || '';
      const minutes = parts[1] || '';
      
      // If hours exist but no minutes, add "00"
      if (hours && !minutes) {
        return `${hours}:00`;
      }
      
      // Limit to 2 digits for hours and 2 digits for minutes
      const h = hours.slice(0, 2);
      const m = minutes.slice(0, 2);
      return `${h}:${m}`;
    }
    
    // If no colon, try to format based on length
    if (cleaned.length <= 2) {
      // If 1-2 digits, treat as hours and add :00
      return `${cleaned}:00`;
    } else if (cleaned.length === 3) {
      // If 3 digits, treat as H:MM
      return `${cleaned[0]}:${cleaned.slice(1, 3)}`;
    } else if (cleaned.length >= 4) {
      // If 4+ digits, treat as HH:MM
      return `${cleaned.slice(0, 2)}:${cleaned.slice(2, 4)}`;
    }
    
    return cleaned;
  };

  const validateTime = (time: string): string => {
    if (!time) return t('behavior.salesTime.error.timeRequired');
    
    const parts = time.split(':');
    if (parts.length !== 2) return t('behavior.salesTime.error.timeFormat');
    
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    
    if (isNaN(hours)) return t('behavior.salesTime.error.timeHour');
    if (hours < 0) return t('behavior.salesTime.error.timeHourMin');
    if (hours > 23) return t('behavior.salesTime.error.timeHourMax');
    
    if (isNaN(minutes)) return t('behavior.salesTime.error.timeMinute');
    if (minutes < 0) return t('behavior.salesTime.error.timeMinuteMin');
    if (minutes > 59) return t('behavior.salesTime.error.timeMinuteMax');
    
    return '';
  };

  const validateTimeRange = (start: string, end: string): string => {
    if (!start || !end) return '';
    
    const startParts = start.split(':');
    const endParts = end.split(':');
    
    if (startParts.length !== 2 || endParts.length !== 2) return '';
    
    const startHours = parseInt(startParts[0], 10);
    const startMinutes = parseInt(startParts[1], 10);
    const endHours = parseInt(endParts[0], 10);
    const endMinutes = parseInt(endParts[1], 10);
    
    if (isNaN(startHours) || isNaN(startMinutes) || isNaN(endHours) || isNaN(endMinutes)) {
      return '';
    }
    
    const startTotal = startHours * 60 + startMinutes;
    const endTotal = endHours * 60 + endMinutes;
    
    if (startTotal >= endTotal) {
      return t('behavior.salesTime.error.timeStartBeforeEnd');
    }
    
    return '';
  };

  const handleTimeChange = (index: number, field: 'start' | 'end', value: string) => {
    // Simply update the value without any formatting or validation
    setTimeRanges(prev => prev.map((range, i) => 
      i === index ? { ...range, [field]: value } : range
    ));
    // Clear errors for this field when user starts typing
    setTimeErrors(prev => prev.map((error, i) => 
      i === index ? { ...error, [field]: '' } : error
    ));
  };

  const handleAddTimeRange = () => {
    setTimeRanges(prev => [...prev, { start: '', end: '' }]);
    setTimeErrors(prev => [...prev, { start: '', end: '' }]);
  };

  const handleRemoveTimeRange = (index: number) => {
    setTimeRanges(prev => prev.filter((_, i) => i !== index));
    setTimeErrors(prev => prev.filter((_, i) => i !== index));
  };

  const handleDeleteRestTime = async (weekday: string, start: string, end: string) => {
    try {
      await api.delete(`/auto-response/schedule/${weekday}`, { data: { start, end } });
      enqueueSnackbar(t('behavior.salesTime.success'), { variant: 'success', autoHideDuration: 3000 });
      fetchRestTimes();
    } catch (e) {
      enqueueSnackbar(t('behavior.salesTime.error.errorDelete'), { variant: 'error', autoHideDuration: 3000 });
    }
  };

  const handleConfirmRestSchedule = async () => {
    try {
      setSubmittingSchedule(true);

      // Initialize errors array
      const errors: Array<{ start: string; end: string }> = timeRanges.map(() => ({ start: '', end: '' }));
      let hasErrors = false;

      // Format and validate each time range
      const formattedRanges = timeRanges.map((range, idx) => {
        const formattedStart = formatTime(range.start);
        const formattedEnd = formatTime(range.end);
        
        // Validate start time
        const startError = validateTime(formattedStart);
        if (startError) {
          errors[idx].start = startError;
          hasErrors = true;
        }
        
        // Validate end time
        const endError = validateTime(formattedEnd);
        if (endError) {
          errors[idx].end = endError;
          hasErrors = true;
        }
        
        // Validate time range (start before end) - only if both are valid
        if (!startError && !endError) {
          const rangeError = validateTimeRange(formattedStart, formattedEnd);
          if (rangeError) {
            errors[idx].start = rangeError;
            errors[idx].end = rangeError;
            hasErrors = true;
          }
        }
        
        return {
          start: formattedStart,
          end: formattedEnd
        };
      });

      // Set errors to display
      setTimeErrors(errors);
      
      // Update timeRanges with formatted values if there are errors (so user can see formatted values)
      if (hasErrors) {
        setTimeRanges(formattedRanges);
        setSubmittingSchedule(false);
        return;
      }

      // Validation helpers for additional checks
      const toMinutes = (t: string) => {
        const [h, m] = t.split(':').map(Number);
        return h * 60 + m;
      };
      const hasOverlaps = (ranges: Array<{ start: string; end: string }>) => {
        const sorted = [...ranges]
          .map(r => ({ s: toMinutes(r.start), e: toMinutes(r.end) }))
          .sort((a, b) => a.s - b.s);
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i].s < sorted[i - 1].e) return true;
        }
        return false;
      };

      // Check for overlaps
      if (hasOverlaps(formattedRanges)) {
        enqueueSnackbar(t('behavior.salesTime.error.timeOverlaps'), { variant: 'error', autoHideDuration: 3000 });
        setSubmittingSchedule(false);
        return;
      }

      const cleanedRanges = formattedRanges.map((r) => ({ start: r.start, end: r.end }));

      const payload = [
        {
          weekday: selectedRestDay,
          time_ranges: cleanedRanges,
        },
      ];

      await api.post('/auto-response/schedule/add', payload);

      enqueueSnackbar(t('behavior.salesTime.success'), { variant: 'success', autoHideDuration: 3000 });

                      // Reset form to initial state
      setTimeRanges([{ start: '', end: '' }]);
      setTimeErrors([{ start: '', end: '' }]);
      setSelectedRestDay('SATURDAY');
      setShowTimeConfigurator(false);
      fetchRestTimes();
    } catch (error) {
      enqueueSnackbar(t('behavior.salesTime.error.errorSubmit'), { variant: 'error', autoHideDuration: 4000 });
    } finally {
      setSubmittingSchedule(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      // آماده سازی filters برای ارسال - مطمئن شویم که تمام filters موجود هستند
      
      const filtersToSend: { [key: string]: boolean } = {};
      availableFilters.forEach((filter) => {
        // تبدیل صریح به boolean
        const value = Boolean(rejexCheckboxes[filter.name]);
        filtersToSend[filter.name] = value;
      });
      
      
      // ارسال همزمان هر دو behavior و behavior filter
      await Promise.all([
        behavior.updateBehavior(behaviorSettings),
        behavior.updateBehaviorFilter({ filters: filtersToSend }),
      ]);
      
      enqueueSnackbar(t('behavior.success'), { 
          variant: 'success',
          autoHideDuration: 4000,
          style: {
            padding: '10px',
            marginTop: '20px', // Add margin top
          },
      });
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      enqueueSnackbar(t('behavior.error.saveError'), { 
        variant: 'error',
        autoHideDuration: 4000,
        style: {
          padding: '10px',
          marginTop: '20px', // Add margin top
        },
      });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AgentBehavior, value: string) => {
    setBehaviorSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRejexCheckboxes({
      ...rejexCheckboxes,
      [event.target.name]: event.target.checked,
    });
  };

  const salesMethodologies = [
    {
      key: 'CONSULTATIVE',
      title: t('behavior.salesMethodology.consultative.title'),
      role: t('behavior.salesMethodology.consultative.role'),
      goal: t('behavior.salesMethodology.consultative.goal'),
      tone: t('behavior.salesMethodology.consultative.tone'),
      structure: t('behavior.salesMethodology.consultative.structure'),
      description: t('behavior.salesMethodology.consultative.description'),
    },
    {
      key: 'TRANSACTIONAL',
      title: t('behavior.salesMethodology.transactional.title'),
      role: t('behavior.salesMethodology.transactional.role'),
      goal: t('behavior.salesMethodology.transactional.goal'),
      tone: t('behavior.salesMethodology.transactional.tone'),
      structure: t('behavior.salesMethodology.transactional.structure'),
      description: t('behavior.salesMethodology.transactional.description'),
    },
    {
      key: 'SOLUTION_BASED',
      title: t('behavior.salesMethodology.solutionBased.title'),
      role: t('behavior.salesMethodology.solutionBased.role'),
      goal: t('behavior.salesMethodology.solutionBased.goal'),
      tone: t('behavior.salesMethodology.solutionBased.tone'),
      structure: t('behavior.salesMethodology.solutionBased.structure'),
      description: t('behavior.salesMethodology.solutionBased.description'),
    },
    {
      key: 'CHALLENGER',
      title: t('behavior.salesMethodology.challenger.title'),
      role: t('behavior.salesMethodology.challenger.role'),
      goal: t('behavior.salesMethodology.challenger.goal'),
      tone: t('behavior.salesMethodology.challenger.tone'),
      structure: t('behavior.salesMethodology.challenger.structure'),
      description: t('behavior.salesMethodology.challenger.description'),
    },
    {
      key: 'SPIN',
      title: t('behavior.salesMethodology.spin.title'),
      role: t('behavior.salesMethodology.spin.role'),
      goal: t('behavior.salesMethodology.spin.goal'),
      tone: t('behavior.salesMethodology.spin.tone'),
      structure: t('behavior.salesMethodology.spin.structure'),
      description: t('behavior.salesMethodology.spin.description'),
    },
    {
      key: 'SANDLER',
      title: t('behavior.salesMethodology.sandler.title'),
      role: t('behavior.salesMethodology.sandler.role'),
      goal: t('behavior.salesMethodology.sandler.goal'),
      tone: t('behavior.salesMethodology.sandler.tone'),
      structure: t('behavior.salesMethodology.sandler.structure'),
      description: t('behavior.salesMethodology.sandler.description'),
    },
  ];

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  const typographyResponsive = {
  '& .MuiTypography-h5': { fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' } },
  '& .MuiTypography-body1': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiTypography-body2': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } },
  '& .MuiTypography-caption': { fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' } },
  '& .MuiTypography-subtitle1': { fontSize: { xs: '0.85rem', sm: '0.9rem', md: '0.95rem' } },
  '& .MuiTypography-subtitle2': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiTableCell-root': { fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } },
  '& .MuiTab-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } },
  '& .MuiFormControlLabel-label': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiAlert-message': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiDialogTitle-root': { fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' } },
  '& .MuiMenuItem-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiButton-root': { fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.875rem' } },
  '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
  '& .MuiInputLabel-root': { fontSize: { xs: '0.8rem', sm: '0.85rem', md: '0.9rem' } },
};

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column', ...typographyResponsive }}>
      <Box sx={{ p: 2 }}>
        <div className="first-step">
        <Typography variant="h5" component="h1" gutterBottom sx={{ fontSize: { xs: '1.2rem', sm: '1.2rem', md: '1.2rem' } }}>
          {t('behavior.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 4, fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.85rem' } }}>
          {t('behavior.description')}
        </Typography>
        </div>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {t('behavior.success')}
          </Alert>
        )}

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={currentTab} 
            onChange={(_e, newValue) => setCurrentTab(newValue)}
            aria-label="behavior tabs"
          >
            <Tab label= {t('behavior.tabs.tab1')} />
            <Tab label= {t('behavior.tabs.tab2')} />
          </Tabs>
        </Box>

        {/* Tab Panel 0: عمومی (General) */}
        {currentTab === 0 && (
          <Box>
            {/* Flexbox container for Conversion Style and Length of Response sections */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
          {/* Conversion Style Section */}
          <Paper sx={{ p: 3, flex: '1 1 300px', minWidth: '300px' }} className="second-step">
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('behavior.conversionStyle.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
              {t('behavior.conversionStyle.description')}
            </Typography>
            
            <Box sx={{mt: 2}}>
            <FormControl component="fieldset">
              <RadioGroup
                value={behaviorSettings.conversion_style || ''}
                onChange={(e) => handleChange('conversion_style', e.target.value)}
              >
                <FormControlLabel
                  value="PROACTIVE"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('behavior.conversionStyle.proactive.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('behavior.conversionStyle.proactive.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="REACTIVE"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('behavior.conversionStyle.reactive.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('behavior.conversionStyle.reactive.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>
            </Box>
          </Paper>

          {/* Length of response section */}
          <Paper sx={{ p: 3, flex: '1 1 300px', minWidth: '300px' }} className="third-step">
            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              {t('behavior.lengthOfResponse.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
              {t('behavior.lengthOfResponse.description')}
            </Typography>
            
            <Box sx={{mt: 2}}>
            <FormControl component="fieldset">
              <RadioGroup
                value={behaviorSettings.answer_length || ''}
                onChange={(e) => handleChange('answer_length', e.target.value)}
              >
                <FormControlLabel
                  value="SHORT"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('behavior.lengthOfResponse.short.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('behavior.lengthOfResponse.short.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="MEDIUM"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('behavior.lengthOfResponse.medium.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('behavior.lengthOfResponse.medium.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  value="LONG"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {t('behavior.lengthOfResponse.long.title')}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('behavior.lengthOfResponse.long.description')}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 2, alignItems: 'flex-start' }}
                />
              </RadioGroup>
            </FormControl>
            </Box>
          </Paper>
        </Box>

        {/* Conversion rejex section */}
        <Paper sx={{ p: 3, mb: 3 }} className="fourth-step">
          <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }} fontWeight="bold">
            {t('behavior.conversionStyle.rejex.title')}
          </Typography> 
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
            {t('behavior.conversionStyle.rejex.description')}
          </Typography>
          
          <Box sx={{mt: 2}}>
            <FormGroup>
              {availableFilters.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('behavior.conversionStyle.rejex.noFilters')}
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={2}>
                  {availableFilters.map((filter) => (
                  <Grid item xs={12} sm={6} md={4} key={filter.name}>
                    <Card 
                      sx={{ 
                        border: rejexCheckboxes[filter.name] ? 2 : 1,
                        borderColor: rejexCheckboxes[filter.name] ? 'primary.main' : 'divider',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 2,
                        }
                      }}
                      onClick={() => {
                        setRejexCheckboxes({
                          ...rejexCheckboxes,
                          [filter.name]: !rejexCheckboxes[filter.name],
                        });
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Checkbox
                              checked={rejexCheckboxes[filter.name] || false}
                              onChange={handleCheckboxChange}
                              name={filter.name}
                              color="primary"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {filter.label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' } }}>
                                {filter.description}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip 
                            label={rejexCheckboxes[filter.name] ? t('behavior.conversionStyle.rejex.active') : t('behavior.conversionStyle.rejex.inactive')} 
                            color={rejexCheckboxes[filter.name] ? 'success' : 'default'}
                            size="small" 
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  ))}
                </Grid>
              )}
            </FormGroup>
          </Box>
        </Paper>

        {/* Sales Time Section */}
        <Paper sx={{ p: 3, mb: 3 }} className="sixth-step">
          <Typography variant="subtitle1" gutterBottom fontWeight="bold">
            {t('behavior.salesTime.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
            {t('behavior.salesTime.description')}
          </Typography>

          <Paper sx={{ p: 0, my: 3 , border:0, boxShadow: 'none'}}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={!showTimeConfigurator ? <AddIcon /> : undefined}
                onClick={() => {
                  if (showTimeConfigurator) {
                    setTimeRanges([{ start: '', end: '' }]);
                    setTimeErrors([{ start: '', end: '' }]);
                    setSelectedRestDay('SATURDAY');
                    setShowTimeConfigurator(false);
                  } else {
                    setShowTimeConfigurator(true);
                  }
                }}
                color={showTimeConfigurator ? 'error' : 'primary'}
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                  px: { xs: 1, sm: 2 },
                  py: { xs: 0.5, sm: 1 },
                  minHeight: { xs: 32, sm: 36 },
                  '& .MuiButton-startIcon': {
                    mr: { xs: 0.5, sm: 1 },
                    '& > *:nth-of-type(1)': {
                      fontSize: { xs: 16, sm: 18, md: 20 },
                    },
                  },
                }}
              >
                {showTimeConfigurator ? t('behavior.salesTime.cancel') : t('behavior.salesTime.addRestTime')}
              </Button>
            </Box>
            
            <Divider sx={{ my: 3 }} />
            <Box>
              {showTimeConfigurator && (
                <Box sx={{ overflowX: 'auto', width: '100%' }}>
                  <Grid container spacing={2} sx={{ borderBottom: '1px solid #cfd8dc', pb: 2, minWidth: 'fit-content' }}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ display: 'flex', alignItems: 'start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ mr: 2, fontWeight: 'bold' }}>
                            {t('behavior.salesTime.restDay')}:
                          </Typography>
                          <Select
                            value={selectedRestDay}
                            onChange={(e) => setSelectedRestDay(e.target.value as string)}
                            sx={{ 
                              width: { xs: 100, sm: 110 }, 
                              height: { xs: 32, sm: 40 },
                              fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                              '& .MuiSelect-select': {
                                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                                py: { xs: 0.5, sm: 1 }
                              }
                            }}
                          >
                            <MenuItem value="SATURDAY">{t('behavior.salesTime.days.saturday')}</MenuItem>
                            <MenuItem value="SUNDAY">{t('behavior.salesTime.days.sunday')}</MenuItem>
                            <MenuItem value="MONDAY">{t('behavior.salesTime.days.monday')}</MenuItem>
                            <MenuItem value="TUESDAY">{t('behavior.salesTime.days.tuesday')}</MenuItem>
                            <MenuItem value="WEDNESDAY">{t('behavior.salesTime.days.wednesday')}</MenuItem>
                            <MenuItem value="THURSDAY">{t('behavior.salesTime.days.thursday')}</MenuItem>
                            <MenuItem value="FRIDAY">{t('behavior.salesTime.days.friday')}</MenuItem>
                          </Select>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={7} md={5}>
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'flex-start', sm: 'flex-start' } }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', marginTop: { xs: 0, sm: 1 } }}>
                          {t('behavior.salesTime.restTime')}:
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          {timeRanges.map((range, idx) => (
                            <Box key={idx} sx={{ mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <input
                                    type="text"
                                    value={range.start}
                                    onChange={(e) => handleTimeChange(idx, 'start', e.target.value)}
                                    placeholder="HH:MM"
                                    style={{
                                      width: isSmallScreen ? '100px' : '120px',
                                      padding: isSmallScreen ? '6px 8px' : '8px 10px',
                                      borderRadius: 8,
                                      border: timeErrors[idx]?.start ? '1px solid #f44336' : '1px solid #cfd8dc',
                                      fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                      direction: 'ltr',
                                      textAlign: 'left'
                                    }}
                                  />
                                  {timeErrors[idx]?.start && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: '#f44336', 
                                        fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                        mt: 0.5,
                                        display: 'block',
                                        width: isSmallScreen ? '100px' : '120px'
                                      }}
                                    >
                                      {timeErrors[idx].start}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{ mx: 1, mt: 1 }}>تا</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                  <input
                                    type="text"
                                    value={range.end}
                                    onChange={(e) => handleTimeChange(idx, 'end', e.target.value)}
                                    placeholder="HH:MM"
                                    style={{
                                      width: isSmallScreen ? '100px' : '120px',
                                      padding: isSmallScreen ? '6px 8px' : '8px 10px',
                                      borderRadius: 8,
                                      border: timeErrors[idx]?.end ? '1px solid #f44336' : '1px solid #cfd8dc',
                                      fontSize: isSmallScreen ? '0.7rem' : '0.8rem',
                                      direction: 'ltr',
                                      textAlign: 'left'
                                    }}
                                  />
                                  {timeErrors[idx]?.end && (
                                    <Typography 
                                      variant="caption" 
                                      sx={{ 
                                        color: '#f44336', 
                                        fontSize: { xs: '0.6rem', sm: '0.65rem', md: '0.7rem' },
                                        mt: 0.5,
                                        display: 'block',
                                        width: isSmallScreen ? '100px' : '120px'
                                      }}
                                    >
                                      {timeErrors[idx].end}
                                    </Typography>
                                  )}
                                </Box>
                                {idx > 0 && (
                                  <Button
                                    onClick={() => handleRemoveTimeRange(idx)}
                                    color="error"
                                    variant="text"
                                    size="small"
                                    sx={{ minWidth: 0, p: 0.5, mt: 0.5 }}
                                    aria-label={`remove-time-${idx}`}
                                  >
                                    <RemoveCircleOutlineIcon />
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={5} md={3}>
                      <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center'}}>
                        <Fab 
                          color="primary" 
                          size="small" 
                          aria-label="add" 
                          
                          onClick={handleAddTimeRange}
                          sx={{
                            width: { xs: 32, sm: 40 },
                            height: { xs: 32, sm: 40 },
                            minWidth: { xs: 32, sm: 40 },
                            minHeight: { xs: 32, sm: 40 },
                            maxWidth: { xs: 32, sm: 40 },
                            maxHeight: { xs: 32, sm: 40 },
                            padding: { xs: 0, sm: 'inherit' },
                            borderRadius: '50%',
                            boxSizing: 'border-box',
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: 'none',
                            },
                            '&.MuiFab-root': {
                              padding: { xs: 0, sm: 'inherit' },
                              boxShadow: 'none',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                            }
                          }}
                        >
                          <AddIcon />
                        </Fab>
                        <Fab 
                          color="success" 
                          size="small" 
                          aria-label="confirm" 
                          sx={{ 
                            ml: 1,
                            width: { xs: 32, sm: 40 },
                            height: { xs: 32, sm: 40 },
                            minWidth: { xs: 32, sm: 40 },
                            minHeight: { xs: 32, sm: 40 },
                            maxWidth: { xs: 32, sm: 40 },
                            maxHeight: { xs: 32, sm: 40 },
                            padding: { xs: 0, sm: 'inherit' },
                            borderRadius: '50%',
                            boxSizing: 'border-box',
                            boxShadow: 'none',
                            '&:hover': {
                              boxShadow: 'none',
                            },
                            '&.MuiFab-root': {
                              padding: { xs: 0, sm: 'inherit' },
                              boxShadow: 'none',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' }
                            }
                          }} 
                          onClick={handleConfirmRestSchedule} 
                          disabled={submittingSchedule}
                        >
                          {submittingSchedule ? (
                            <CircularProgress size={isSmallScreen ? 16 : 20} sx={{ color: 'white' }} />
                          ) : (
                            <CheckIcon />
                          )}
                        </Fab>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
              
              {restTimes.length === 0 && (
                <Box sx={{ textAlign: 'center',display:'flex' ,minHeight: '200px', alignItems: 'center', justifyContent: 'center' }}>
                    {t('behavior.salesTime.emptyDescription')}
                </Box>
              )}

              {/* Rest Times Table - static UI (no API yet) */}
              {restTimes.length > 0 && (
                <Paper sx={{ p: 0 , mt: 3}}>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{t('behavior.salesTime.table.restDay')}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{t('behavior.salesTime.table.startTime')}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{t('behavior.salesTime.table.endTime')}</TableCell>
                          <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{t('behavior.salesTime.table.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {restTimes.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{entry.rest_day}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{displayTime(entry.start_time)}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>{displayTime(entry.end_time)}</TableCell>
                            <TableCell sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.8rem' } }}>
                              <IconButton
                                color="error"
                                size="small"
                                aria-label="delete-rest-time"
                                onClick={() => handleDeleteRestTime(entry.weekday_key, entry.start_time, entry.end_time)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </Box>

          </Paper>
        </Paper>
          </Box>
        )}

        {/* Tab Panel 1: روش شناسی فروش (Sales Methodology) */}
        {currentTab === 1 && (
          <Box>
            {/* Sales Methodology Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <div className="fifth-step">
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                {t('behavior.salesMethodology.title')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 3 }}>
                {t('behavior.salesMethodology.description')}
              </Typography>
              </div>
              
              <Box sx={{mt: 2}}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <RadioGroup
                    value={behaviorSettings.sales_methodology || ''}
                    onChange={(e) => handleChange('sales_methodology', e.target.value)}
                  >
                    <Grid container spacing={2}>
                      {salesMethodologies.map((methodology) => (
                        <Grid item xs={12} md={6} key={methodology.key}>
                          <Card 
                            sx={{ 
                              height: '100%',
                              border: behaviorSettings.sales_methodology === methodology.key ? 2 : 1,
                              borderColor: behaviorSettings.sales_methodology === methodology.key ? 'primary.main' : 'divider',
                              cursor: 'pointer',
                              '&:hover': {
                                borderColor: 'primary.main',
                              }
                            }}
                            onClick={() => handleChange('sales_methodology', methodology.key)}
                          >
                            <CardContent>
                              <FormControlLabel
                                value={methodology.key} 
                                control={<Radio />}
                                label=""
                                sx={{ m: 0, top: 8, right: 8 ,display: 'none'}}

                              />
                              <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold">
                                {methodology.title}
                              </Typography>
                              <Typography variant="caption" sx={{ mb: 2 }}>
                                {methodology.description}
                              </Typography>
                              <Divider sx={{ my: 2 }} />
                              <Box sx={{ mb: 1 }}>
                              <Chip label={`${t('behavior.salesMethodology.labels.role')} ${methodology.role}`} size="small" sx={{ mr: 1, mb: 1 }} />                        </Box>
                              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                <strong>{t('behavior.salesMethodology.labels.goal')}</strong> {methodology.goal}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                                <strong>{t('behavior.salesMethodology.labels.tone')}</strong> {methodology.tone}
                              </Typography>
                              <Typography variant="caption" display="block">
                                <strong>{t('behavior.salesMethodology.labels.structure')}</strong> {methodology.structure}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </RadioGroup>
                </FormControl>
              </Box>
            </Paper>
          </Box>
        )}

          

        {/* Save Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3}}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? t('behavior.saving') : t('behavior.save')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const Behavior: React.FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_behavior}>
      <BehaviorContent />
    </TourProvider>
  );
};

export default Behavior; 